import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"
import { urlWithQuery } from "../helpers/url_with_query"

// The capacity board's Bedarf grid (Planning::DemandGrid::BoardComponent): PT demand per site,
// trade and period, edited the way every BauTask user knows from Excel.
//
// - A click SELECTS a cell; Shift+click or Shift+arrows extend the selection to a rectangle.
// - Typing a number, Enter, F2 or a double-click start editing; typing replaces the value.
// - Enter commits and moves down, Tab commits and moves right, Escape cancels, Delete clears.
// - A value typed while several cells are selected goes into all of them — one request, one undo.
// - Shift+F2 (Excel's "comment") or the "Notiz zur Zelle" button opens the note of the cell.
//
// One controller for the whole grid, listening by delegation: a controller per cell is this board's
// recurring performance trap. Cells carry only their column and value; the period comes from the
// header cell of the column, the plannable and trade from the row. The selection is remembered as
// {rowKey, col} so it survives the server re-rendering the site after a write.
const DECIMAL = /^\d+([.,]\d{1,2})?$/
const MOVES = {
  ArrowDown: [1, 0],
  ArrowUp: [-1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
}

export default class extends Controller {
  static targets = [
    "scroller",
    "table",
    "rowTemplate",
    "addDialog",
    "addTrade",
    "rangeDialog",
    "rangeSite",
    "rangeTrade",
    "rangeFrom",
    "rangeTo",
    "rangePerDay",
    "rangeWorkingDays",
    "rangeNotes",
    "rangeSubmit",
  ]

  static values = {
    cellsUrl: String,
    rangeUrl: String,
    noteUrl: String,
    canPlan: Boolean,
    invalidMessage: String,
  }

  connect() {
    this.anchor = null
    this.focus = null
    this.editing = null
    this.addSiteId = null
    this.onClick = this.handleClick.bind(this)
    this.onDoubleClick = this.handleDoubleClick.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    this.onStreamRender = this.handleStreamRender.bind(this)
    this.onDialogKeydown = this.handleDialogKeydown.bind(this)
    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("dblclick", this.onDoubleClick)
    this.element.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("dblclick", this.onDoubleClick)
    this.element.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
    document.removeEventListener("keydown", this.onDialogKeydown, true)
  }

  // --- Geometry -------------------------------------------------------------------------------

  rows() {
    return this.hasTableTarget
      ? Array.from(this.tableTarget.querySelectorAll("tr[data-grid-row]"))
      : []
  }

  rowByKey(key) {
    return this.rows().find((row) => row.dataset.rowKey === key) ?? null
  }

  cellAt(row, col) {
    return row?.querySelector(`td[data-col="${col}"]`) ?? null
  }

  columnCount() {
    return this.hasTableTarget ? this.tableTarget.querySelectorAll("thead th[data-col]").length : 0
  }

  period(col) {
    const header = this.tableTarget.querySelector(`thead th[data-col="${col}"]`)
    return { from: header.dataset.from, to: header.dataset.to }
  }

  // The selected rectangle as [{ row, cell, col }], anchor and focus included.
  selectedCells() {
    if (!this.anchor || !this.focus) return []
    const rows = this.rows()
    const rowIndexes = [this.anchor.rowKey, this.focus.rowKey].map((key) =>
      rows.findIndex((row) => row.dataset.rowKey === key),
    )
    if (rowIndexes.includes(-1)) return []
    const [top, bottom] = rowIndexes.sort((a, b) => a - b)
    const [left, right] = [this.anchor.col, this.focus.col].sort((a, b) => a - b)
    const cells = []
    for (let index = top; index <= bottom; index++) {
      for (let col = left; col <= right; col++) {
        const cell = this.cellAt(rows[index], col)
        if (cell) cells.push({ row: rows[index], cell, col })
      }
    }
    return cells
  }

  // --- Selection ------------------------------------------------------------------------------

  select(row, col, { extend = false } = {}) {
    const position = { rowKey: row.dataset.rowKey, col }
    this.focus = position
    if (!extend || !this.anchor) this.anchor = position
    this.paint()
    this.cellAt(row, col)?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  paint() {
    this.element
      .querySelectorAll("td[data-selected]")
      .forEach((cell) => cell.removeAttribute("data-selected"))
    this.selectedCells().forEach(({ cell }) => cell.setAttribute("data-selected", ""))
  }

  clearSelection() {
    this.anchor = null
    this.focus = null
    this.paint()
  }

  move(rowStep, colStep, { extend = false } = {}) {
    if (!this.focus) return
    const rows = this.rows()
    const index = rows.findIndex((row) => row.dataset.rowKey === this.focus.rowKey)
    if (index === -1) return
    const row = rows[Math.min(Math.max(index + rowStep, 0), rows.length - 1)]
    const col = Math.min(Math.max(this.focus.col + colStep, 0), this.columnCount() - 1)
    this.select(row, col, { extend })
  }

  // A site was re-rendered: put the selection back on the same row key and column. An edit open
  // in the replaced site (typed while the previous write was in flight) moves into the new cell
  // with its text and caret — the removal is not a commit, so its blur is ignored meanwhile.
  handleStreamRender(event) {
    const render = event.detail.render
    event.detail.render = async (stream) => {
      const editing = this.editing
      this.rendering = true
      try {
        await render(stream)
      } finally {
        this.rendering = false
      }
      if (editing && editing === this.editing && !editing.input.isConnected)
        this.resumeEdit(editing)
      if (this.focus && !this.rowByKey(this.focus.rowKey)) this.clearSelection()
      else this.paint()
    }
  }

  resumeEdit(editing) {
    const cell = this.cellAt(this.rowByKey(editing.position.rowKey), editing.position.col)
    if (!cell) {
      this.editing = null
      this.scrollerTarget.focus({ preventScroll: true })
      return
    }
    const { selectionStart, selectionEnd } = editing.input
    cell.appendChild(editing.input)
    editing.cell = cell
    editing.input.focus()
    editing.input.setSelectionRange(selectionStart, selectionEnd)
  }

  // --- Events ---------------------------------------------------------------------------------

  handleClick(event) {
    const add = event.target.closest("[data-add-trade]")
    if (add) return this.openAddDialog(add.dataset.siteId)

    const cell = event.target.closest("td[data-col]")
    const row = cell?.closest("tr[data-grid-row]")
    if (!row || cell.contains(this.editing?.input)) return

    if (this.editing) this.commitEdit()
    this.select(row, Number(cell.dataset.col), { extend: event.shiftKey })
    this.scrollerTarget.focus({ preventScroll: true })
  }

  handleDoubleClick(event) {
    const cell = event.target.closest("td[data-col]")
    if (!cell?.closest("tr[data-grid-row]") || this.editing) return
    this.startEdit(null)
  }

  handleKeydown(event) {
    if (this.editing || this.dialogOpen()) return
    if (event.target !== this.scrollerTarget) return

    if (event.key === "F2" && event.shiftKey) return this.prevent(event, () => this.openNote())
    if (!this.focus) return

    if (MOVES[event.key])
      return this.prevent(event, () => this.move(...MOVES[event.key], { extend: event.shiftKey }))
    if (event.key === "Tab") return this.prevent(event, () => this.move(0, event.shiftKey ? -1 : 1))
    if (event.key === "Escape") return this.prevent(event, () => this.clearSelection())
    if (event.key === "Enter" || event.key === "F2")
      return this.prevent(event, () => this.startEdit(null))
    if (event.key === "Delete" || event.key === "Backspace")
      return this.prevent(event, () => this.write("0"))
    if (/^[\d.,]$/.test(event.key) && !event.metaKey && !event.ctrlKey) {
      return this.prevent(event, () => this.startEdit(event.key))
    }
  }

  prevent(event, action) {
    event.preventDefault()
    action()
  }

  // --- Editing --------------------------------------------------------------------------------

  // Opens an input in the focused cell: with the typed character (type-over) or the current value.
  startEdit(typed) {
    if (!this.canPlanValue || !this.focus) return
    const cell = this.cellAt(this.rowByKey(this.focus.rowKey), this.focus.col)
    if (!cell) return

    const input = document.createElement("input")
    input.type = "text"
    input.inputMode = "decimal"
    input.className =
      "absolute inset-0 w-full border-0 bg-white px-1.5 text-right text-sm tabular-nums focus:ring-2 focus:ring-green-600"
    input.value = typed ?? this.displayValue(cell.dataset.value)
    input.addEventListener("keydown", (event) => this.handleEditKeydown(event))
    input.addEventListener("blur", () => {
      if (!this.rendering) this.commitEdit()
    })
    cell.appendChild(input)
    this.editing = {
      cell,
      input,
      position: { ...this.focus },
      original: input.value,
      typed: typed !== null,
    }
    input.focus()
    if (typed === null) input.select()
  }

  handleEditKeydown(event) {
    if (event.key === "Escape") return this.prevent(event, () => this.cancelEdit())
    if (event.key === "Enter") return this.prevent(event, () => this.commitEdit([1, 0]))
    if (event.key === "Tab")
      return this.prevent(event, () => this.commitEdit([0, event.shiftKey ? -1 : 1]))
  }

  cancelEdit() {
    const editing = this.editing
    this.editing = null
    editing?.input.remove()
    this.scrollerTarget.focus({ preventScroll: true })
  }

  commitEdit(step = null) {
    const editing = this.editing
    if (!editing) return
    this.editing = null
    const text = editing.input.value.trim()
    editing.input.remove()
    this.scrollerTarget.focus({ preventScroll: true })

    const changed = editing.typed || text !== editing.original || this.selectedCells().length > 1
    if (changed) this.write(text === "" ? "0" : text)
    if (step) this.move(...step)
  }

  displayValue(raw) {
    const value = Number(raw)
    return value ? String(value).replace(".", ",") : ""
  }

  // Sends one value for every selected cell. Invalid input is refused here, before any request.
  async write(text) {
    if (!this.canPlanValue) return
    if (!DECIMAL.test(text)) return this.flash(this.invalidMessageValue)

    const cells = this.selectedCells().map(({ row, col }) => ({
      plannable_type: row.dataset.plannableType,
      plannable_id: row.dataset.plannableId,
      trade_id: row.dataset.tradeId,
      date_from: this.period(col).from,
      date_to: this.period(col).to,
      value: text.replace(",", "."),
    }))
    if (cells.length === 0) return

    await planningMutation({
      url: this.cellsUrlValue,
      method: "PATCH",
      body: { cells },
      component: "planning-demand-grid",
    })
  }

  flash(message) {
    document.dispatchEvent(new CustomEvent("planning:flash", { detail: { message } }))
  }

  // --- Notes ----------------------------------------------------------------------------------

  openNote() {
    const position = this.focus
    const row = position && this.rowByKey(position.rowKey)
    const cell = this.cellAt(row, position?.col)
    if (!cell) return

    const { from, to } = this.period(position.col)
    const query = new URLSearchParams({
      plannable_type: row.dataset.plannableType,
      plannable_id: row.dataset.plannableId,
      trade_id: row.dataset.tradeId,
      date_from: from,
      date_to: to,
    })
    document.dispatchEvent(
      new CustomEvent("planning:edit-demand-note", {
        detail: { url: urlWithQuery(this.noteUrlValue, query), text: cell.dataset.notes || "" },
      }),
    )
  }

  // --- "+ Gewerk" -----------------------------------------------------------------------------

  openAddDialog(siteId) {
    this.addSiteId = siteId
    this.selectController(this.addTradeTarget)?.clear?.()
    this.showDialog(this.addDialogTarget)
  }

  // Adds an empty row for the chosen trade to the site (or selects the row it already has). The
  // server renders it for real once a value is typed into it.
  addRow() {
    const tradeId = this.addTradeTarget.value
    if (!tradeId || !this.addSiteId) return

    const key = `Site-${this.addSiteId}-${tradeId}`
    const row = this.rowByKey(key) ?? this.buildRow(key, tradeId)
    this.closeDialogs()
    if (row) {
      this.select(row, 0)
      this.scrollerTarget.focus({ preventScroll: true })
    }
  }

  buildRow(key, tradeId) {
    const body = this.tableTarget.querySelector(`tbody[data-site-id="${this.addSiteId}"]`)
    if (!body) return null

    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    const cellTemplate = row.querySelector("[data-cell-template]")
    Object.assign(row.dataset, {
      rowKey: key,
      plannableType: "Site",
      plannableId: this.addSiteId,
      tradeId,
      siteId: this.addSiteId,
    })
    row.querySelector("[data-row-label]").textContent =
      this.addTradeTarget.selectedOptions[0]?.textContent ?? ""
    for (let col = 0; col < this.columnCount(); col++) {
      const cell = cellTemplate.cloneNode(true)
      cell.removeAttribute("data-cell-template")
      cell.dataset.col = col
      cell.dataset.value = "0"
      row.appendChild(cell)
    }
    cellTemplate.remove()
    body.insertBefore(row, body.querySelector("tr[data-add-row]"))
    return row
  }

  // --- "Bedarf über Zeitraum" -----------------------------------------------------------------

  // Opens the dialog, pre-filled from the selection when there is one: its site, trade and period.
  openRange() {
    const row = this.focus && this.rowByKey(this.focus.rowKey)
    if (row) {
      this.selectController(this.rangeSiteTarget)?.setSelected(row.dataset.siteId)
      if (row.dataset.plannableType === "Site")
        this.selectController(this.rangeTradeTarget)?.setSelected(row.dataset.tradeId)
      const cols = [this.anchor.col, this.focus.col].sort((a, b) => a - b)
      this.rangeFromTarget.value = this.period(cols[0]).from
      this.rangeToTarget.value = this.period(cols[1]).to
    }
    this.showDialog(this.rangeDialogTarget)
  }

  async submitRange() {
    this.rangeSubmitTarget.disabled = true
    const { ok } = await planningMutation({
      url: this.rangeUrlValue,
      method: "PATCH",
      body: {
        plannable_type: "Site",
        plannable_id: this.rangeSiteTarget.value,
        trade_id: this.rangeTradeTarget.value,
        date_from: this.rangeFromTarget.value,
        date_to: this.rangeToTarget.value,
        per_day: this.rangePerDayTarget.value,
        working_days_only: this.rangeWorkingDaysTarget.checked ? "1" : "0",
        notes: this.rangeNotesTarget.value,
      },
      component: "planning-demand-grid",
    })
    this.rangeSubmitTarget.disabled = false
    if (ok) {
      this.rangeNotesTarget.value = ""
      this.closeDialogs()
    }
  }

  // --- Dialogs --------------------------------------------------------------------------------

  // Overlays like the board's other dialogs (not modal <dialog>s, which would cover the error toast).
  // Unlike showModal() they move no focus on their own: in on open, back to the grid on close, or
  // the grid's keys (which only listen on the scroller) stay dead until the next click.
  showDialog(dialog) {
    dialog.classList.remove("hidden")
    dialog.classList.add("flex")
    document.addEventListener("keydown", this.onDialogKeydown, true)
    dialog.querySelector(".ss-main, input, textarea, button")?.focus()
  }

  closeDialogs() {
    if (!this.dialogOpen()) return

    this.dialogs().forEach((dialog) => {
      dialog.classList.add("hidden")
      dialog.classList.remove("flex")
    })
    document.removeEventListener("keydown", this.onDialogKeydown, true)
    this.scrollerTarget.focus({ preventScroll: true })
  }

  // Captured, so it sees a SlimSelect still open: that Escape only closes the dropdown. SlimSelect
  // marks every Escape as handled, open or not, so defaultPrevented cannot tell the two apart. Its
  // open list lives on <body>, not in the dialog — with the overlay up, it can only be the dialog's.
  handleDialogKeydown(event) {
    if (event.key !== "Escape") return
    if (document.querySelector(".ss-content.ss-open")) return

    this.closeDialogs()
  }

  dialogOpen() {
    return this.dialogs().some((dialog) => !dialog.classList.contains("hidden"))
  }

  dialogs() {
    return [
      this.hasAddDialogTarget && this.addDialogTarget,
      this.hasRangeDialogTarget && this.rangeDialogTarget,
    ].filter(Boolean)
  }

  // A click on the overlay itself, not on the panel, is a click on the backdrop.
  backdrop(event) {
    if (event.target === event.currentTarget) this.closeDialogs()
  }

  selectController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "select")
  }
}
